import { useState, useMemo, useEffect } from 'react';
import { PropertyFilterProps } from '@cloudscape-design/components';
import { ActivityItem, ActivityFilterCriteria, FilterOption } from '../types';
import { getWorkflowDisplayName } from '../../../constants/workflow-tags';

interface UseActivityFilteringProps {
  activities: ActivityItem[];
  filteringQuery: PropertyFilterProps.Query;
  isServerSide?: boolean;
}

interface UseActivityFilteringReturn {
  filteredActivities: ActivityItem[];
  filteringProperties: PropertyFilterProps.FilteringProperty[];
  filteringOptions: PropertyFilterProps.FilteringOption[];
  isLoading: boolean;
}

export const useActivityFiltering = ({
  activities,
  filteringQuery,
  isServerSide = false,
}: UseActivityFilteringProps): UseActivityFilteringReturn => {
  const [isLoading, setIsLoading] = useState(true);

  // Simulate loading on initial mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500); // 1.5 seconds loading simulation

    return () => clearTimeout(timer);
  }, []);

  // Define filtering properties for PropertyFilter
  const filteringProperties: PropertyFilterProps.FilteringProperty[] = useMemo(() => [
    {
      key: 'workflowType',
      propertyLabel: 'Workflow Type',
      groupValuesLabel: 'Workflow Type values',
      operators: ['=', '!=', ':', '!:'],
    },
    {
      key: 'workflowName',
      propertyLabel: 'Workflow Name',
      groupValuesLabel: 'Workflow Name values',
      operators: ['=', '!=', ':', '!:'],
    },
    {
      key: 'status',
      propertyLabel: 'Status',
      groupValuesLabel: 'Status values',
      operators: ['=', '!='],
    },
  ], []);

  // Generate filtering options dynamically from activities
  const filteringOptions: PropertyFilterProps.FilteringOption[] = useMemo(() => {
    const workflowTypes = Array.from(new Set(activities.map(a => a.category).filter(Boolean)));
    const workflowNames = Array.from(new Set(activities.map(a => a.reportName).filter(Boolean)));
    const statuses = Array.from(new Set(activities.map(a => a.status).filter(Boolean)));

    return [
      ...workflowTypes.map(type => ({
        propertyKey: 'workflowType',
        value: type!,
        label: getWorkflowDisplayName(type!),
      })),
      ...workflowNames.map(name => ({
        propertyKey: 'workflowName',
        value: name!,
      })),
      ...statuses.map(status => ({
        propertyKey: 'status',
        value: status!,
      })),
    ];
  }, [activities]);

  // Filter activities based on PropertyFilter query
  const filteredActivities = useMemo(() => {
    if (isServerSide) {
      return activities;
    }

    if (!filteringQuery.tokens || filteringQuery.tokens.length === 0) {
      return activities;
    }

    return activities.filter(activity => {
      const results = filteringQuery.tokens.map(token => {
        const { propertyKey, operator, value } = token;
        let itemValue: string | undefined;

        switch (propertyKey) {
          case 'workflowType':
            itemValue = activity.category;
            break;
          case 'workflowName':
            itemValue = activity.reportName;
            break;
          case 'status':
            itemValue = activity.status;
            break;
          default:
            return true;
        }

        if (!itemValue) return false;

        const lowerItemValue = itemValue.toLowerCase();
        const lowerValue = value.toLowerCase();

        switch (operator) {
          case '=':
            return lowerItemValue === lowerValue;
          case '!=':
            return lowerItemValue !== lowerValue;
          case ':':
            return lowerItemValue.includes(lowerValue);
          case '!:':
            return !lowerItemValue.includes(lowerValue);
          default:
            return true;
        }
      });

      return filteringQuery.operation === 'and'
        ? results.every(r => r)
        : results.some(r => r);
    });
  }, [activities, filteringQuery, isServerSide]);

  return {
    filteredActivities,
    filteringProperties,
    filteringOptions,
    isLoading,
  };
};
